import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { DatePicker, Popconfirm, Select, Table } from "antd";
import { Edit, Trash2 } from "feather-icons-react/build/IconComponents";
import { Calendar, Info, LifeBuoy } from "feather-icons-react/build/IconComponents";
import { Controller, useForm, useWatch } from "react-hook-form";
import InputField from "../../components/Input";
import { DateFormat, Debounce, emailRegex } from "../../../utils";
import dayjs from "dayjs";
import DeliveryTimeSlotServices from "../../../services/DeliveryTimeSlot";
import SalePersonServices from "../../../services/SalePerson";
import DeliveryTypeServices from "../../../services/DeliveryType";
import BrandServices from "../../../services/Brand";
import ProductServices from "../../../services/Product";
import PaymentHeadServices from "../../../services/PaymentHead";
import { v4 as uuidv4 } from 'uuid'
import { useSelector } from "react-redux";
import ServiceTypeServices from "../../../services/ServiceType";
import SelectField from "../../components/Select";
import SaleInvoiceServices from "../../../services/SaleInvoice";
import StoreServices from "../../../services/Store";
import StateServices from "../../../services/State";
import { ErrorToaster, SuccessToaster } from "../../components/toaster/Toaster";
import IMask from "imask";
import CategoryServices from "../../../services/Category";
import ProductForm from "../../inventory/products/productform";
import { statuses } from "../../../core/json/statuses";
import Html5QrcodePlugin from "./Html5QrcodePlugin";
import RefundExchangeForm from '../../refund-exchange/forms/RefundExchangeForm';
import Debug from "../../../utils/Debug"

// Utility function for deep equality check
const isEqual = (a, b) => {
  if (a == b) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!isEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

// Utility function to compute diff object with only changed fields
const diff = (original, current) => {
  const result = {};
  for (const key in current) {
    if (!isEqual(original[key], current[key])) {
      result[key] = current[key];
    }
  }
  return result;
};

const Form = ({ onSubmit, loading }) => {

  const route = all_routes;
  const { id } = useParams();
  const { state } = useLocation();

  const { userData } = useSelector((state) => {
    return {
      userData: state.user_data
    }
  });

  const { register, handleSubmit, formState: { errors }, control, setValue, getValues } = useForm();
  const { register: register2, handleSubmit: handleSubmit2, formState: { errors: errors2 }, control: control2, reset: reset2, setValue: setValue2, getValues: getValues2 } = useForm();
  const { register: register3, handleSubmit: handleSubmit3, formState: { errors: errors3 }, reset: reset3, setValue: setValue3 } = useForm();

  const [pageLoading, setPageLoading] = useState(false);
  const [productModalLoading, setProductModalLoading] = useState(false);

  // *For Update Invoice Row
  const [updateData, setUpdateData] = useState();

  // *For Delivery Time Slots
  const [deliveryTimeSlots, setDeliveryTimeSlots] = useState([]);
  const [selectedDeliveryTimeSlot, setSelectedDeliveryTimeSlot] = useState(null);

  // *For Delivery Type
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(null);
  const [selectedShippingType, setSelectedShippingType] = useState(null);

  // *For Store
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);

  // *For Status
  const [selectedStatus, setSelectedStatus] = useState(null);

  // *For Sale Persons
  const [salePersons, setSalePersons] = useState([]);
  const [selectedSalePerson, setSelectedSalePerson] = useState([]);

  // *For Brands
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // *For Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // *For Products
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // *For Product Variants
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // *For Payment Method
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([{ id: uuidv4(), payment_head: null, card_last_digit: '', received_amount: '', isNew: true }]);
  const [totalReceivedAmount, setTotalReceivedAmount] = useState(0);

  // *For Service Type
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState(null);

  // *For Delivery Discount
  const [deliveryDiscount, setDeliveryDiscount] = useState(0);

  // *Is delivery Address Same
  const [sameDeliveryAddress, setSameDeliveryAddress] = useState(false);

  // *For Invoice Locked
  const [isInvoiceLocked, setIsInvoiceLocked] = useState(false);
  // console.log(' Form => isInvoiceLocked:', isInvoiceLocked)

  // *For Invoice Rows
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [totalRegPrice, setTotalRegPrice] = useState(0);
  const [saleTax, setSaleTax] = useState(0);
  const [saleTaxRate, setSaleTaxRate] = useState(0);

  // *For Recycling Fee Lines
  const [recyclingFeeLines, setRecyclingFeeLines] = useState([]);

  // *For Discounts
  const [discountTotal, setDiscountTotal] = useState(0);
  const [discountRows, setDiscountRows] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountDescription, setDiscountDescription] = useState('');
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  const [discountValue, setDiscountValue] = useState('');
  const [totalSavings, setTotalSavings] = useState(0);

  // Submit a discount row (adds to invoiceRows as a special row)
  const submitDiscountRow = () => {
    try {
      const recyclingTotal = (recyclingFeeLines || []).reduce((acc, fee) => acc + (fee?.totalFee ? fee.totalFee : 0), 0);
      const base = (itemSubTotal || 0) + (recyclingTotal || 0) + (deliveryTotal || 0);
      const val = parseFloat(discountValue) || 0;
      const computedAmount = (discountType === 'percentage') ? (base * (val / 100)) : val;
      console.log('🚀 => submitDiscountRow => computedAmount:', computedAmount)
      const newRow = {
        id: `discount-${uuidv4()}`,
        brand: { name: '' },
        category: { name: '' },
        product: { name: discountDescription || 'Discount' },
        variant: { name: '' },
        description: discountDescription,
        quantity: 1,
        regPrice: '',
        salePrice: computedAmount,
        extendedTotal: computedAmount,
        deliveryType: { name: '' },
        isFee: false,
        isDiscount: true,
        discountType,
        discountValue: val,
        discountAmount: computedAmount,
      };
      setInvoiceRows((prev) => [...prev, newRow]);
      // Immediately update discountTotal so UI reacts without waiting for effect ordering
      console.log('computedAmount==', computedAmount)
      // setDiscountTotal(prev => Number(prev || 0) + Number(computedAmount || 0));
      // reset modal fields
      setDiscountDescription('');
      setDiscountType('amount');
      setDiscountValue('');
      setShowDiscountModal(false);
    } catch (error) {
      console.log('submitDiscountRow => error:', error);
    }
  }


  // *For Zip Code
  const [billingTaxDetail, setBillingTaxDetail] = useState();
  const [shippingTaxDetail, setShippingTaxDetail] = useState();

  // For QR Code Scan
  const [scanOpen, setScanOpen] = useState(false);

  const onNewScanResult = (decodedText, decodedResult) => {
    console.log("decodedText", decodedText);
    console.log("decodedResult", decodedResult);

    // ✅ set value in react-hook-form field
    setValue3("barcode", decodedText, { shouldValidate: true });

    // optionally close scanner once scanned
    setScanOpen(false);
  };

  // *For Sale Invoice Data (for modal)
  const [saleInvoice, setSaleInvoice] = useState();

  // *For Get Delivery Time Slot
  const getDeliveryTimeSlots = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await DeliveryTimeSlotServices.getDeliveryTimeSlots(params)
      setDeliveryTimeSlots(data)
    } catch (error) {
      console.log('getDeliveryTimeSlots => error:', error)
    }
  }

  // *For Get Delivery Type 
  const getDeliveryTypes = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await DeliveryTypeServices.getDeliveryTypes(params)
      setDeliveryTypes(data)
    } catch (error) {
      console.log('getDeliveryTypes => error:', error)
    }
  }

  // *For Get Sale Persons
  const getSalePersons = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await SalePersonServices.getSalePersons(params)
      setSalePersons(data)
    } catch (error) {
      console.log('getSalePersons => error:', error)
    }
  }

  // *For Get Store
  const getStores = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await StoreServices.getStores(params)
      setStores(data)
    } catch (error) {
      console.log('getStores => error:', error)
    }
  }

  // *For Get Brands
  const getBrands = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await BrandServices.getBrands(params)
      setBrands(data)
    } catch (error) {
      console.log('getBrands => error:', error)
    }
  }

  // *For Get Categories
  const getCategories = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await CategoryServices.getCategories(params)
      setCategories(data)
    } catch (error) {
      console.log('getCategories => error:', error)
    }
  }

  // *For Get Products
  const getProducts = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true,
        brand: selectedBrand?.id || '',
        category: selectedCategory?.id || '',
      }
      const { data } = await ProductServices.getProducts(params)
      setProducts(data)
    } catch (error) {
      console.log('getProducts => error:', error)
    }
  }

  const createProductFromSale = async (formData) => {
    setProductModalLoading(true);
    try {
      await ProductServices.createProduct(formData);
      SuccessToaster('Product created successfully');
      await getProducts();
      window.bootstrap?.Modal?.getInstance(document.getElementById('open-product-modal'))?.hide();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setProductModalLoading(false);
    }
  };

  // *For Get Payment Method
  const getPaymentHeads = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await PaymentHeadServices.getPaymentHeads(params)
      setPaymentMethods(data)
    } catch (error) {
      console.log('getPaymentHeads => error:', error)
    }
  }

  // *For Get Service Type
  const getServiceTypes = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await ServiceTypeServices.getServiceTypes(params)
      setServiceTypes(data)
    } catch (error) {
      console.log('getServiceTypes => error:', error)
    }
  }

  // *For Create Brand
  const createBrand = async (name) => {
    try {
      let obj = {
        name: name,
        remarks: "",
        is_active: true
      }
      const { data } = await BrandServices.createBrand(obj)
      getBrands()
      setSelectedBrand({ id: data[0].id, name: data[0].name });
      setValue("brand", data[0].name);
    } catch (error) {
      console.log('createBrand => error:', error)
    }
  }

  // *For Create Category
  const createCategory = async (name) => {
    try {
      let obj = {
        name: name,
        remarks: "",
        is_active: true,
        staticOptions: {}
      }
      const { data } = await CategoryServices.createCategory(obj)
      getCategories()
      setSelectedCategory({ id: data[0].id, name: data[0].name });
      setValue("category", data[0].name);
    } catch (error) {
      console.log('createCategory => error:', error)
    }
  }

  // *For Submit Form
  const submitForm = async (formData) => {
    try {
      const salePersonIds = []
      const invoice_payments = []
      const sale_invoice_items = []
      // Only include actual product rows as sale_invoice_items (exclude fees & discounts)
      invoiceRows?.filter(r => !r.isFee && !r.isDiscount).forEach(e => {
        let item = {
          // brand_id: e?.brand?.id,
          item_id: e?.product?.id,
          variant_id: e?.variant?.id,
          // size_id: e?.size?.id,
          description: e?.description,
          quantity: e?.quantity,
          regular_price: Number(e?.regPrice),
          sales_price: Number(e?.salePrice),
          extended_total: Number(e?.extendedTotal),
          // shipping_cost: 5, // need to be remove
          delivery_type_id: e?.deliveryType?.id,
          tax_percentage: parseFloat(saleTax),
          tax_amount: Number(((e?.salePrice * e?.quantity) * saleTax).toFixed(3)),
        }
        sale_invoice_items.push(item)
      })
      selectedSalePerson.forEach(e => {
        salePersonIds.push(e?.id)
      })
      const validPayments = invoicePayments.filter(e => e?.received_amount > 0 && e?.isNew);
      validPayments.forEach(e => {
        invoice_payments.push({
          payment_head_id: e?.payment_head?.id,
          payment_ref: e?.card_last_digit || e?.payment_ref || '',
          received_amount: e?.received_amount
        })
      })
      let obj = {
        store_id: userData?.store?.id,
        billing_name: formData?.billingName,
        billing_last_name: formData?.billingLastName,
        billing_city: billingTaxDetail?.city?.id,
        billing_state: billingTaxDetail?.state?.id,
        billing_zip_code: formData?.billingZipCode,
        billing_address: formData?.billingAddress,
        billing_phone_number_one: formData?.billingPhone1,
        billing_phone_number_two: formData?.billingPhone2,
        billing_email: formData?.billingEmail,
        shipping_name: formData?.shippingName ?? formData?.billingName,
        shipping_last_name: formData?.shippingLastName ?? formData?.billingLastName,
        shipping_city: shippingTaxDetail?.city?.id ?? billingTaxDetail?.city?.id,
        shipping_state: shippingTaxDetail?.state?.id ?? billingTaxDetail?.state?.id,
        shipping_zip_code: formData?.shippingZipCode ?? formData?.billingZipCode,
        shipping_address: formData?.shippingAddress ?? formData?.billingAddress,
        shipping_phone_number_one: formData?.shippingPhone1 ?? formData?.billingPhone1,
        shipping_phone_number_two: formData?.shippingPhone2 ?? formData?.billingPhone2,
        shipping_email: formData?.shippingEmail ?? formData?.billingEmail,
        same_delivery_details: sameDeliveryAddress,
        order_date: dayjs(formData?.order_date).format('YYYY-MM-DD'),
        delivery_date: dayjs(formData?.delivery_date).format('YYYY-MM-DD'),
        delivery_time_slot_id: selectedDeliveryTimeSlot?.id,
        delivery_day: dayjs(formData?.delivery_date).format('dddd').toUpperCase(),
        delivery_type_id: selectedDeliveryType?.id,
        salespersons: salePersonIds,
        status: selectedStatus?.id,
        sale_invoice_items: sale_invoice_items,
        delivery_charges: selectedServiceType?.price,
        delivery_charges_discount: deliveryDiscount,
        // service_type: selectedServiceType?.id,
        total: total,
        // include discounts in total_savings (discounts increase savings)
        total_savings: discountTotal,
        sub_total: subTotal,
        sales_tax: parseFloat(saleTaxRate).toFixed(2),
        // total_sale = (subTotal - discounts) + tax
        total_sale: (Number(parseFloat(subTotal || 0)) - Number(parseFloat(discountTotal || 0)) + Number(parseFloat(saleTaxRate || 0))).toFixed(2),
        balance_amount: ((Number(parseFloat(subTotal || 0)) - Number(parseFloat(discountTotal || 0)) + Number(parseFloat(saleTaxRate || 0))) - (Number(parseFloat(totalReceivedAmount || 0)))).toFixed(2),
        comments: formData?.comments,
        instructions: formData?.instructions,
        invoice_payments: invoice_payments,
        tax_percentage: parseFloat(saleTax),
        // send discounts captured client-side
        discounts: invoiceRows
          .filter(r => r.isDiscount)
          .map(r => ({
            description: r.description || '',
            type: r.discountType === 'percentage' ? 'percentage' : 'amount',
            value: Number(r.discountValue || r.discountAmount || 0),
            amount: Number(r.discountAmount || 0),
          })),
      }
      if (id) {
        obj.service_type_id = selectedServiceType?.id
      } else {
        obj.service_type = selectedServiceType?.id
      }
      // For updates, compute diff to send only changed fields
      if (id && saleInvoice) {
        // Build original object from saleInvoice data
        const originalSalePersonIds = saleInvoice?.salespersons?.map(sp => sp.id) || [];
        const originalInvoicePayments = saleInvoice?.invoice_payments?.map(p => ({
          payment_head_id: p.payment_head?.id,
          payment_ref: p.payment_ref || '',
          received_amount: p.received_amount
        })) || [];
        const originalSaleInvoiceItems = saleInvoice?.sale_invoice_items?.map(item => ({
          item_id: item.item?.id,
          variant_id: item.variant?.id,
          description: item.description,
          quantity: item.quantity,
          regular_price: Number(item.regular_price),
          sales_price: Number(item.sales_price),
          extended_total: Number(item.extended_total),
          delivery_type_id: item.delivery_type?.id,
          tax_percentage: parseFloat(item.tax_percentage),
          tax_amount: Number(item.tax_amount),
        })) || [];
        const originalDiscounts = saleInvoice?.discounts?.map(d => ({
          description: d.description || '',
          type: d.type,
          value: Number(d.value || 0),
          amount: Number(d.amount || 0),
        })) || [];

        const originalObj = {
          store_id: saleInvoice?.store?.id,
          billing_name: saleInvoice?.billing_name,
          billing_last_name: saleInvoice?.billing_last_name,
          billing_city: saleInvoice?.billing_city,
          billing_state: saleInvoice?.billing_state,
          billing_zip_code: saleInvoice?.billing_zip_code,
          billing_address: saleInvoice?.billing_address,
          billing_phone_number_one: saleInvoice?.billing_phone_number_one,
          billing_phone_number_two: saleInvoice?.billing_phone_number_two,
          billing_email: saleInvoice?.billing_email,
          shipping_name: saleInvoice?.shipping_name,
          shipping_last_name: saleInvoice?.shipping_last_name,
          shipping_city: saleInvoice?.shipping_city,
          shipping_state: saleInvoice?.shipping_state,
          shipping_zip_code: saleInvoice?.shipping_zip_code,
          shipping_address: saleInvoice?.shipping_address,
          shipping_phone_number_one: saleInvoice?.shipping_phone_number_one,
          shipping_phone_number_two: saleInvoice?.shipping_phone_number_two,
          shipping_email: saleInvoice?.shipping_email,
          same_delivery_details: saleInvoice?.same_delivery_details,
          order_date: saleInvoice?.order_date,
          delivery_date: saleInvoice?.delivery_date,
          delivery_time_slot_id: saleInvoice?.delivery_time_slot?.id,
          delivery_day: saleInvoice?.delivery_day,
          delivery_type_id: saleInvoice?.delivery_type?.id,
          salespersons: originalSalePersonIds,
          status: saleInvoice?.status,
          sale_invoice_items: originalSaleInvoiceItems,
          delivery_charges: saleInvoice?.delivery_charges,
          delivery_charges_discount: saleInvoice?.delivery_charges_discount,
          service_type_id: saleInvoice?.service_type?.id,
          total: saleInvoice?.total,
          total_savings: saleInvoice?.total_savings,
          sub_total: saleInvoice?.sub_total,
          sales_tax: saleInvoice?.sales_tax,
          total_sale: saleInvoice?.total_sale,
          balance_amount: saleInvoice?.balance_amount,
          comments: saleInvoice?.comments,
          instructions: saleInvoice?.instructions,
          invoice_payments: originalInvoicePayments,
          tax_percentage: saleInvoice?.tax_percentage,
          discounts: originalDiscounts,
        };

        // Compute diff
        const changedFields = diff(originalObj, obj);
        onSubmit(changedFields);
      } else {
        onSubmit(obj);
      }
    } catch (error) {
      console.log('submitForm => error:', error)
    }
  }

  // *For Submit Invoice Row
  const submitInvoiceRow = async (formData) => {
    try {
      let obj = {
        brand: selectedBrand,
        category: selectedCategory,
        product: selectedProduct,
        variant: selectedVariant,
        description: formData?.description,
        quantity: parseInt(formData?.quantity),
        regPrice: parseFloat(formData?.regPrice),
        salePrice: parseFloat(formData?.salePrice),
        extendedTotal: parseFloat(formData?.salePrice) * parseInt(formData?.quantity),
        deliveryType: selectedShippingType
      }
      if (updateData?.id) {
        const index = invoiceRows.findIndex((e) => e.id === updateData?.id)

        if (index !== -1) {
          const updatedInvoiceRows = [...invoiceRows];
          updatedInvoiceRows[index] = { ...updatedInvoiceRows[index], ...obj };
          setInvoiceRows(updatedInvoiceRows);
        }

      } else {
        const updateInvoiceRow = [...invoiceRows]
        updateInvoiceRow.push({ id: uuidv4(), ...obj })
        setInvoiceRows(updateInvoiceRow)
      }
      resetAddRowFields()
      const btn = document.getElementById('modalClose')
      btn.click();
    } catch (error) {
      console.log('submitForm => error:', error)
    }
  }

  // *For Delete Row
  const deleteInvoiceRow = (id) => {
    try {
      const index = invoiceRows.findIndex((e) => e.id === id)
      let updateInvoiceRow = [...invoiceRows]
      updateInvoiceRow.splice(index, 1);
      setInvoiceRows(updateInvoiceRow)
    } catch (error) {
      console.log('deleteRow => error:', error)
    }
  }

  // Watch all billing fields
  const billingValues = useWatch({
    control,
    name: [
      "billingName",
      "billingLastName",
      "billingPhone1",
      "billingPhone2",
      "billingEmail",
      "billingZipCode",
      "billingCity",
      "billingState",
      "billingAddress",
    ]
  });

  // Sync shipping when sameDeliveryAddress is true
  useEffect(() => {
    if (sameDeliveryAddress) {
      setValue("shippingName", billingValues[0]);
      setValue("shippingLastName", billingValues[1]);
      setValue("shippingPhone1", billingValues[2]);
      setValue("shippingPhone2", billingValues[3]);
      setValue("shippingEmail", billingValues[4]);
      setValue("shippingZipCode", billingValues[5]);
      setValue("shippingCity", billingValues[6]);
      setValue("shippingState", billingValues[7]);
      setValue("shippingAddress", billingValues[8]);
    }
  }, [billingValues, sameDeliveryAddress]);

  // Discounts are added via the modal only; no inline-edit helpers are used.
  // submitDiscountRow handles adding a discount row to invoiceRows.

  // *For Handle Reset Add Row Fields
  const resetAddRowFields = () => {
    try {
      reset2()
      setVariants([])
      setUpdateData()
      setSelectedBrand(null)
      setSelectedCategory(null)
      setSelectedProduct(null)
      setSelectedVariant(null)
      setSelectedShippingType(null)
    } catch (error) {
      console.log(' resetAddRowFields => error:', error)
    }
  }

  // --- Enhanced: Interleave fee lines after their respective items ---
  const mergedRows = React.useMemo(() => {
    // Separate base item rows (exclude fees and discounts)
    const baseItems = invoiceRows.filter(r => !r.isFee && !r.isDiscount);

    if (!saleInvoice) {
      // For new invoice, interleave recycling fees after their respective base items when possible
      let result = [...baseItems];
      const usedFeeIndexes = new Set();
      recyclingFeeLines.forEach((fee) => {
        const idx = baseItems.findIndex(row => row.category?.id === fee.categoryId && (!fee.productId || row.product?.id === fee.productId));
        if (idx !== -1 && !usedFeeIndexes.has(idx)) {
          result.splice(idx + 1, 0, {
            id: `recycling-fee-${fee.categoryId}-${fee.productId || ''}`,
            brand: { name: '' },
            category: { name: '' },
            product: { name: fee.description },
            variant: { name: '' },
            description: '',
            quantity: fee.quantity || 1,
            regPrice: '',
            salePrice: fee.unitFee || fee.totalFee,
            extendedTotal: fee.totalFee,
            isFee: true,
            feeType: fee.description,
          });
          usedFeeIndexes.add(idx);
        } else {
          result.push({
            id: `recycling-fee-${fee.categoryId}-${fee.productId || ''}`,
            brand: { name: '' },
            category: { name: '' },
            product: { name: fee.description },
            variant: { name: '' },
            description: '',
            quantity: fee.quantity || 1,
            regPrice: '',
            salePrice: fee.unitFee || fee.totalFee,
            extendedTotal: fee.totalFee,
            isFee: true,
            feeType: fee.description,
          });
        }
      });

      // Append discount rows at the end (client-side discounts)
      const clientDiscounts = invoiceRows.filter(r => r.isDiscount);
      clientDiscounts.forEach(d => result.push(d));

      return result;
    }

    // For existing invoice, use backend fee lines and then append discounts at end
    const items = baseItems;
    const fees = (saleInvoice.fees || []);
    let result = [];
    const usedFeeIds = new Set();
    items.forEach(item => {
      result.push({ ...item, isFee: false });
      // Find all fees for this item
      const itemFees = fees.filter(fee => fee.sale_invoice_item && fee.sale_invoice_item.id === item.id);
      itemFees.forEach(fee => {
        result.push({
          id: `fee-${fee.id}`,
          brand: { name: '' },
          category: { name: '' },
          product: { name: fee.description },
          variant: { name: '' },
          description: '',
          quantity: fee.quantity || 1,
          regPrice: '',
          salePrice: fee.unit_fee || fee.amount,
          extendedTotal: fee.amount,
          isFee: true,
          feeType: fee.description,
        });
        usedFeeIds.add(fee.id);
      });
    });
    // Add any fees not linked to an item at the end
    const unlinkedFees = fees.filter(fee => !fee.sale_invoice_item || !fee.sale_invoice_item.id || usedFeeIds.has(fee.id) === false);
    unlinkedFees.forEach(fee => {
      if (!usedFeeIds.has(fee.id)) {
        result.push({
          id: `fee-${fee.id}`,
          brand: { name: '' },
          category: { name: '' },
          product: { name: fee.description },
          variant: { name: '' },
          description: '',
          quantity: fee.quantity || 1,
          regPrice: '',
          salePrice: fee.unit_fee || fee.amount,
          extendedTotal: fee.amount,
          isFee: true,
          feeType: fee.description,
        });
      }
    });

    // Append backend discounts (persisted) then client-side discounts
    const backendDiscounts = (saleInvoice.discounts || []).map(d => ({
      id: `discount-backend-${d.id}`,
      brand: { name: '' },
      category: { name: '' },
      product: { name: d.description || 'Discount' },
      variant: { name: '' },
      description: d.description,
      quantity: d.quantity || 1,
      regPrice: '',
      salePrice: Number(d.amount || d.value || 0),
      extendedTotal: Number(d.amount || d.value || 0),
      isFee: false,
      isDiscount: true,
      discountType: d.type === 'percentage' ? 'percentage' : 'amount',
      discountValue: Number(d.value || d.amount || 0),
      discountAmount: Number(d.amount || d.value || 0),
    }));
    const clientDiscounts = invoiceRows.filter(r => r.isDiscount);

    backendDiscounts.forEach(d => result.push(d));
    clientDiscounts.forEach(d => result.push(d));

    return result;
  }, [invoiceRows, recyclingFeeLines, saleInvoice]);

  // --- Enhanced: Add Fee Type column and custom row styling ---
  const columns = [
    {
      title: "Brand",
      dataIndex: "brand",
      render: (text) => (
        <span>{text?.name}</span>
      ),
    },
    {
      title: "Product",
      dataIndex: "product",
      render: (text, record) => {
        // For discount rows show the description (if provided), otherwise product name
        return (
          <span style={record.isFee ? { fontStyle: 'italic', color: '#ff9f43' } : (record.isDiscount ? { fontStyle: 'italic', color: '#2b9fda' } : {})}>
            {record.isDiscount ? (record.description || text?.name) : (text?.name)}
            {record.isFee && (
              <span className="badge badge-warning ms-2" style={{ background: '#fff5ec', color: '#ff9f43', border: '1px solid #ff9f43', fontSize: 12, fontWeight: 500 }}>FEE</span>
            )}
            {record.isDiscount && !record.isFee && (
              <span className="badge badge-info ms-2" style={{ background: '#e8f7ff', color: '#2b9fda', border: '1px solid #2b9fda', fontSize: 12, fontWeight: 500 }}>DISCOUNT</span>
            )}
          </span>
        );
      },
    },
    // {
    //   title: "Fee Type",
    //   dataIndex: "feeType",
    //   render: (text, record) => record.isFee ? <span style={{ color: '#ff9f43', fontStyle: 'italic' }}>{text}</span> : null,
    // },
    {
      title: "Qty",
      dataIndex: "quantity",
      render: (text, record) => (
        <span>{text}</span>
      ),
    },
    {
      title: "Reg Price",
      dataIndex: "regPrice",
      render: (text, record) => (
        <span>{text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}</span>
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      render: (text, record) => <span>{text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}</span>,
    },
    {
      title: "Extended Total",
      dataIndex: "extendedTotal",
      render: (text, record) => (
        <span style={record.isFee ? { color: '#ff9f43', fontWeight: 600 } : {}}>
          {text !== '' ? `$${parseFloat(text).toFixed(2)}` : ''}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => {
        if (record.isFee) return null;
        if (record.isDiscount) {
          return record.isEditing ? (
            <div className="action-table-data d-flex gap-2">
              <button type="button" className="btn btn-sm btn-secondary" onClick={(e) => { e.preventDefault(); saveDiscountRow(record.id); }}>Save</button>
              <button type="button" className="btn btn-sm btn-danger" onClick={(e) => { e.preventDefault(); deleteInvoiceRow(record.id); }}>Delete</button>
            </div>
          ) : (
            <div className="action-table-data d-flex gap-2">
              {/* <button type="button" className="btn btn-sm btn-info" onClick={(e) => { e.preventDefault(); const idx = invoiceRows.findIndex(r => r.id === record.id); if (idx === -1) return; const updated = [...invoiceRows]; updated[idx].isEditing = true; setInvoiceRows(updated); }}>Edit</button> */}
              <button type="button" className="btn btn-sm btn-danger" onClick={(e) => { e.preventDefault(); deleteInvoiceRow(record.id); }}>Delete</button>
            </div>
          );
        }
        return (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <div className="input-block add-lists"></div>
              <Link className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#open-modal"
                onClick={(e) => { e.preventDefault(); setUpdateData(record); }}
              >
                <Edit className="feather-edit" />
              </Link>
              <Link className="confirm-text p-2">
                <Popconfirm title="Sure to delete?" onConfirm={() => deleteInvoiceRow(record.key)}>
                  <Trash2 className="feather-trash-2" />
                </Popconfirm>
              </Link>
            </div>
          </div>
        );
      },
    },
  ];

  // *For Get Sale Invoice Details
  const getSaleInvoiceDetails = async () => {
    setPageLoading(true)
    try {
      const { data } = await SaleInvoiceServices.getSaleInvoiceById(id)
      setSaleInvoice(data[0])

      const saleInvoiceRows = []
      data[0]?.sale_invoice_items.forEach(element => {
        const obj = {
          id: element?.id,
          brand: element?.item?.brand,
          category: element?.item?.category ?? { id: element?.item?.category_id },
          product: element?.item,
          variant: {
            ...element?.variant,
            name: element?.variant && Object.values(element?.variant?.attributes).map(String).join('-').toUpperCase(),
          },
          description: element?.description,
          quantity: parseFloat(element?.quantity),
          regPrice: parseFloat(element?.regular_price),
          salePrice: parseFloat(element?.sales_price),
          extendedTotal: parseFloat(element?.extended_total),
          deliveryType: element?.delivery_type
        }
        saleInvoiceRows.push(obj)
      })
      setInvoiceRows(saleInvoiceRows)
      setInvoicePayments(data[0]?.invoice_payments)
      data[0]?.invoice_payments.forEach(element => {
        setValue(`card_last_digit-${element?.id}`, element?.payment_ref)
        setValue(`received_amount-${element?.id}`, element?.received_amount)
      })
      setSelectedSalePerson(data[0]?.salespersons)
      setSelectedStore(data[0]?.store)
      setValue('store', data[0]?.store?.name)
      setSelectedDeliveryType(data[0]?.delivery_type)
      setValue('deliveryType', data[0]?.delivery_type?.name)
      setSelectedDeliveryTimeSlot(data[0]?.delivery_time_slot)
      setValue('deliveryTimeSlot', data[0]?.delivery_time_slot?.name)
      setSelectedServiceType(data[0]?.service_type)
      setValue('serviceType', data[0]?.service_type?.name)
      const status = statuses.find(status => status.value === data[0]?.status)
      setSelectedStatus({ id: status?.value, name: status?.label })
      setValue('status', data[0]?.status)
      setValue('deliveryDiscount', data[0]?.delivery_charges_discount)
      setDeliveryDiscount(data[0]?.delivery_charges_discount)
      setValue('order_date', dayjs(data[0]?.order_date))
      setValue('delivery_date', dayjs(data[0]?.delivery_date))
      setSameDeliveryAddress(data[0]?.same_delivery_details)
      setValue('billingName', data[0]?.billing_name)
      setValue('billingLastName', data[0]?.billing_last_name)
      setValue('billingAddress', data[0]?.billing_address)
      setValue('billingCity', data[0]?.billing_city)
      setValue('billingState', data[0]?.billing_state)
      setValue('billingPhone1', data[0]?.billing_phone_number_one)
      setValue('billingPhone2', data[0]?.billing_phone_number_two)
      setValue('billingEmail', data[0]?.billing_email)
      setValue('billingZipCode', data[0]?.billing_zip_code)
      getInfoByZipCode(data[0]?.billing_zip_code, 'billing')
      setValue('shippingName', data[0]?.shipping_name)
      setValue('shippingLastName', data[0]?.shipping_last_name)
      setValue('shippingAddress', data[0]?.shipping_address)
      setValue('shippingCity', data[0]?.shipping_city)
      setValue('shippingState', data[0]?.shipping_state)
      setValue('shippingPhone1', data[0]?.shipping_phone_number_one)
      setValue('shippingPhone2', data[0]?.shipping_phone_number_two)
      setValue('shippingEmail', data[0]?.shipping_email)
      setValue('shippingZipCode', data[0]?.shipping_zip_code)
      getInfoByZipCode(data[0]?.shipping_zip_code, 'shipping')
      setValue('comments', data[0]?.comments)
      setValue('instructions', data[0]?.instructions)
      setIsInvoiceLocked(['SHIPPED', 'LOCKED', 'DELIVERED'].includes(data[0]?.status))
    } catch (error) {
      console.log(' getSaleInvoiceDetails => error:', error)
      ErrorToaster(error)
    } finally {
      setPageLoading(false)
    }
  }

  // *For Add Invoice Payment
  const addInvoicePayment = async () => {
    try {
      const updateInvoicePayments = [...invoicePayments]
      let obj = { id: uuidv4(), isNew: true }
      updateInvoicePayments.push(obj)
      setInvoicePayments(updateInvoicePayments)
    } catch (error) {
      console.log('addInvoicePayment => error:', error)
    }
  }

  // *For Delete Invoice Payment
  const deleteInvoicePayment = (id) => {
    try {
      const index = invoicePayments.findIndex((e) => e.id === id)
      let updateInvoicePayment = [...invoicePayments]
      updateInvoicePayment.splice(index, 1);
      setInvoicePayments(updateInvoicePayment)
    } catch (error) {
      console.log(' deleteInvoicePayment => error:', error)
    }
  }

  // *For Update Invoice Payment 
  const handleUpdateInvoicePayment = (value, id, key) => {
    try {
      const index = invoicePayments.findIndex((e) => e.id === id)
      let updateInvoicePayment = [...invoicePayments]
      updateInvoicePayment[index][key] = value
      setInvoicePayments(updateInvoicePayment)
    } catch (error) {
      console.log(' handleUpdateInvoicePayment => error:', error)
    }
  }

  // *For Get State By Zip Code
  const getInfoByZipCode = async (zipCode, type) => {
    try {
      if (zipCode.length == 5) {
        const { data } = await StateServices.getInfoByZipCode(zipCode)
        if (type == 'billing') {
          setValue('billingState', data[0]?.state?.name)
          setValue('billingCity', data[0]?.city?.name)
          setBillingTaxDetail(data[0])
          setSaleTax(data[0]?.tax?.rate)
          if (sameDeliveryAddress) {
            setValue('shippingState', data[0]?.state?.name)
            setValue('shippingCity', data[0]?.city?.name)
            setShippingTaxDetail(data[0])
          }
        } else {
          setValue('shippingState', data[0]?.state?.name)
          setValue('shippingCity', data[0]?.city?.name)
          setShippingTaxDetail(data[0])
        }
      } else {
        return
      }
    } catch (error) {
      console.log(' getInfoByZipCode => error:', error)
      // ErrorToaster(error)
    }
  }

  // *For Get Customer Detail By Phone 
  const getCustomerByPhone = async (phone) => {
    try {
      const result = await SaleInvoiceServices.getCustomerByPhone(phone)
      const { addresses } = result
      if (addresses.length > 0) {
        setSameDeliveryAddress(false)
        setValue('billingName', addresses[0]?.billing_name)
        setValue('billingLastName', addresses[0]?.billing_last_name)
        setValue('billingAddress', addresses[0]?.billing_address)
        // setValue('billingCity', addresses[0]?.billing_city)
        // setValue('billingState', addresses[0]?.billing_state)
        setValue('billingPhone1', addresses[0]?.billing_phone_number_one)
        setValue('billingPhone2', addresses[0]?.billing_phone_number_two)
        setValue('billingEmail', addresses[0]?.billing_email)
        setValue('billingZipCode', addresses[0]?.billing_zip_code)
        getInfoByZipCode(addresses[0]?.billing_zip_code, 'billing')
        setValue('shippingName', addresses[0]?.shipping_name)
        setValue('shippingLastName', addresses[0]?.shipping_last_name)
        setValue('shippingAddress', addresses[0]?.shipping_address)
        // setValue('shippingCity', addresses[0]?.shipping_city)
        // setValue('shippingState', addresses[0]?.shipping_state)
        setValue('shippingPhone1', addresses[0]?.shipping_phone_number_one)
        setValue('shippingPhone2', addresses[0]?.shipping_phone_number_two)
        setValue('shippingEmail', addresses[0]?.shipping_email)
        setValue('shippingZipCode', addresses[0]?.shipping_zip_code)
        getInfoByZipCode(addresses[0]?.shipping_zip_code, 'shipping')
      }
    } catch (error) {
      console.log(' getCustomerByPhone => error:', error)
      // ErrorToaster(error)
    }
  }

  // *For Handle Variation Select
  const handleSelectVariant = () => {
    try {
      const variantName = []
      const allValues = getValues2();
      Object.entries(allValues).map(([key, value]) => {
        if (key.includes('variant') && value != null) {
          variantName.push(value)
        }
      })
      const selectedVariantName = variantName.join('-');
      const variant = selectedProduct?.variants.find(e => Object.values(e?.attributes).map(String).join('-') === selectedVariantName)

      if (variant) {
        const price = variant?.price || 0;
        setValue2('regPrice', price);
        setSelectedVariant({
          id: variant?.id,
          name: Object.values(variant?.attributes).map(String).join('-').toUpperCase(),
          price: price,
          totalOnHold: variant?.totalOnHold,
          totalQuantity: variant?.totalQuantity
        })
      }
    } catch (error) {
      console.log(' handleSelectVariant => error:', error)
    }
  }

  const [showRefundModal, setShowRefundModal] = useState(false);

  useEffect(() => {
    getDeliveryTimeSlots()
    getDeliveryTypes()
    getSalePersons()
    getBrands()
    getCategories()
    getPaymentHeads()
    getServiceTypes()
    getStores()
    setValue('order_date', dayjs())
    setValue('delivery_date', dayjs().add(3, 'day'))
  }, []);

  // --- Calculate item-only subtotal (excluding fees & discounts) ---
  const [itemSubTotal, setItemSubTotal] = useState(0);
  useEffect(() => {
    // Only sum real item extended totals (exclude fee and discount rows)
    const itemTotal = (invoiceRows || [])
      .filter(r => !r.isFee && !r.isDiscount)
      .reduce((acc, item) => acc + (Number(item?.regPrice ? item.regPrice : 0) * Number(item.quantity)), 0);
    setItemSubTotal(itemTotal || 0);
  }, [invoiceRows]);

  // --- Calculate delivery charges/discounts ---
  const [deliveryTotal, setDeliveryTotal] = useState(0);
  useEffect(() => {
    const deliveryCharge = parseFloat(selectedServiceType?.price || 0);
    const delivery = parseFloat(deliveryDiscount || 0) > 0 ? deliveryCharge - parseFloat(deliveryDiscount).toFixed(2) : deliveryCharge;
    setDeliveryTotal(delivery);
  }, [selectedServiceType, deliveryDiscount]);

  // --- Calculate sales tax on item subtotal + delivery + recycling - discounts (pre-tax) ---
  useEffect(() => {
    try {
      const recyclingTotal = recyclingFeeLines?.reduce((acc, fee) => acc + (fee?.totalFee ? fee.totalFee : 0), 0) || 0;
      const taxableBase = (itemSubTotal || 0) + (deliveryTotal || 0) + (recyclingTotal || 0) - (discountTotal || 0);
      if (saleTax > 0) {
        const saletaxrate = taxableBase * parseFloat(saleTax);
        setSaleTaxRate(parseFloat(saletaxrate).toFixed(2));
      } else {
        setSaleTaxRate(0);
      }
    } catch (err) {
      setSaleTaxRate(0);
    }
  }, [itemSubTotal, deliveryTotal, saleTax, discountTotal, recyclingFeeLines]);

  // --- Calculate total: items + fees + delivery (Sub Total remains unchanged by discounts) ---
  useEffect(() => {
    const recyclingTotal = recyclingFeeLines?.reduce((acc, fee) => acc + (fee?.totalFee ? fee.totalFee : 0), 0) || 0;
    const total = itemSubTotal || 0;
    setTotal(total);
    // Sub Total = items + recycling fees + delivery (do not subtract discounts here)
    const computedSubTotal = (itemSubTotal || 0) + (recyclingTotal) + (deliveryTotal || 0);
    setSubTotal(Number(computedSubTotal).toFixed(2));
  }, [itemSubTotal, deliveryTotal, recyclingFeeLines]);

  // // --- Calculate discount total (client-side rows + backend discounts when editing) ---
  // useEffect(() => {
  //   try {
  //     console.log("setTotalSavings", totalSavings);

  //     const clientDiscounts = (invoiceRows || []).filter(r => r.isDiscount).reduce((acc, r) => acc + (Number(r.discountAmount || 0)), 0);
  //     const backendDiscounts = (saleInvoice?.discounts || []).reduce((acc, d) => acc + (Number(d.amount || 0)), 0);
  //     setDiscountTotal(clientDiscounts + backendDiscounts);
  //     setTotalSavings(prevTotalSavings => prevTotalSavings);
  //     // (total - totalRegPrice)+parseFloat(discountTotal || 0))
  //   } catch (err) {
  //     setDiscountTotal(0);
  //   }
  // }, [invoiceRows, saleInvoice, recyclingFeeLines, deliveryTotal]);

  // --- Fix Discount Total Issue
  useEffect(() => {
    if (!invoiceRows || invoiceRows.length === 0) {
      return;
    }
    console.log('invoiceRows==>', invoiceRows)

    const totalSavingsCalc = invoiceRows.reduce((acc, row) => {
      const reg = Number(row.regPrice) || 0;
      const sale = Number(row.salePrice) || 0;
      const qty = Number(row.quantity) || 0;

      return acc + (reg !== 0 ? reg - sale : sale) * qty;
    }, 0);
    console.log('🚀 => totalSavingsCalc:', totalSavingsCalc)

    // console.log("invoiceRows", invoiceRows);
    // console.log("subTotal", subTotal);
    // console.log("total", total);
    // console.log("totalRegPrice", totalRegPrice);
    // console.log("totalSavings", totalSavingsCalc);

    // setTotalSavings(totalSavingsCalc);
    setDiscountTotal(totalSavingsCalc);
  }, [invoiceRows, saleInvoice, recyclingFeeLines, deliveryTotal]);

  useEffect(() => {
    if (updateData) {
      setValue2('brand', updateData?.brand?.name)
      setValue2('category', updateData?.category?.name)
      setValue2('product', updateData?.product?.name)
      setValue2('description', updateData?.description)
      setValue2('quantity', updateData?.quantity)
      setValue2('regPrice', updateData?.regPrice)
      setValue2('salePrice', updateData?.salePrice)
      setValue2('shippingType', updateData?.deliveryType?.name)
      setSelectedBrand(updateData?.brand)
      setSelectedCategory(updateData?.category)
      setSelectedProduct(updateData?.product)
      setSelectedVariant(updateData?.variant)
      setSelectedShippingType(updateData?.deliveryType)

      if (updateData?.product?.variants?.length > 0) {
        const variantId = updateData?.variant?.id
        const variant = updateData?.product?.variants.find(e => e.id === variantId)
        Object.entries(variant.attributes).map(([key, value]) => {
          setValue2(`variant-${key}`, value)
        })
      }
    }
  }, [updateData]);

  useEffect(() => {
    if (id) {
      getSaleInvoiceDetails()
    }
  }, [id]);

  useEffect(() => {
    if (!updateData && (selectedBrand?.id || selectedCategory?.id)) {
      getProducts()
      setSelectedProduct(null)
    }
  }, [selectedBrand, selectedCategory]);

  useEffect(() => {
    if (selectedProduct?.id) {
      const variant = []
      if (selectedProduct?.variant_options) {
        Object.entries(selectedProduct?.variant_options).map(([key, value]) => {
          variant.push({
            name: key,
            options: value.map((e) => { return { label: e, value: e } }),
          })
        })
      }
      setVariants(variant)
      setSelectedVariant(null)
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (state?.phone) {
      getCustomerByPhone(state?.phone)
    }
  }, [state]);

  // --- Real-time recycling fee calculation ---
  useEffect(() => {
    const fetchRecyclingFees = async () => {
      // console.log('shippingTaxDetail?.state?.id', shippingTaxDetail?.state?.id)
      if (!invoiceRows.length || !shippingTaxDetail?.state?.id) {
        setRecyclingFeeLines([]);
        return;
      }
      const items = invoiceRows.map(row => ({
        categoryId: row.category?.id,
        quantity: row.quantity
      }));
      try {
        const data = await SaleInvoiceServices.calculateRecyclingFee(items, shippingTaxDetail?.state?.id);
        setRecyclingFeeLines(data);
      } catch (error) {
        setRecyclingFeeLines([]);
      }
    };
    fetchRecyclingFees();
  }, [invoiceRows, shippingTaxDetail?.state?.id]);

  // --- Merge invoiceRows and fee lines for table display ---
  // useEffect(() => {
  //   const invoiceTotal = invoiceRows?.reduce((acc, item) => acc + (item?.extendedTotal ? item?.extendedTotal : 0), 0)
  //   const totalRegPrice = invoiceRows?.reduce((acc, item) => acc + (item?.regPrice ? item?.regPrice * item.quantity : 0), 0)
  //   const recyclingTotal = recyclingFeeLines?.reduce((acc, fee) => acc + (fee?.totalFee ? fee.totalFee : 0), 0)
  //   setTotal((invoiceTotal || 0))
  //   setTotalSavings(prevTotalSavings => prevTotalSavings + (totalRegPrice - invoiceTotal));
  //   setTotalRegPrice(totalRegPrice || 0)
  // }, [invoiceRows, recyclingFeeLines]);

  const [balanceAmount, setBalanceAmount] = useState(0);
  useEffect(() => {
    const totalReceive = invoicePayments?.reduce((acc, item) => acc + (item?.received_amount ? parseFloat(item?.received_amount) : 0), 0);
    // console.log('🚀 => totalReceive:', totalReceive)
    // console.log('🚀 => subTotal:', subTotal)
    // console.log('🚀 => saleTaxRate:', saleTaxRate)
    setTotalReceivedAmount(totalReceive || 0);
    const bal = (parseFloat(subTotal) - parseFloat(discountTotal || 0) + parseFloat(saleTaxRate)) - (totalReceive || 0);
    // console.log('🚀 => bal:', bal)
    setBalanceAmount(bal);
  }, [invoicePayments, subTotal, saleTaxRate]);

  return (
    <>
      {pageLoading ? (
        <div id="global-loader" className="page-loader">
          <div className="whirly-loader"></div>
        </div>
      ) : (
        <>
          <div className="modal fade" id="open-product-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered custom-modal-two" style={{ maxWidth: '1100px' }}>
              <div className="modal-content">
                <div className="page-wrapper-new p-0">
                  <div className="content">
                    <div className="modal-header border-0 custom-modal-header">
                      <div className="page-title">
                        <h4>{"Add Product"}</h4>
                      </div>
                      <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    </div>
                    <div className="modal-body custom-modal-body new-employee-field">
                      <ProductForm onSubmit={createProductFromSale} loading={productModalLoading} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal fade" id="open-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered custom-modal-two" style={{ maxWidth: '900px' }}>
              <div className="modal-content">
                <div className="page-wrapper-new p-0">
                  <div className="content">
                    <div className="modal-header border-0 custom-modal-header">
                      <div className="page-title">
                        <h4>{updateData?.id ? "Edit Item" : "Add Item"}</h4>
                      </div>
                      <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    </div>
                    <div className="modal-body custom-modal-body new-employee-field">
                      <form onSubmit={handleSubmit2(submitInvoiceRow)}>
                        <div className="row">
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <SelectField
                                addNew={(newValue) => createBrand(newValue)}
                                label={"Brand"}
                                options={brands}
                                selected={selectedBrand}
                                onSelect={(value) => setSelectedBrand(value)}
                                error={errors2?.brand?.message}
                                register={register2("brand", {
                                  required: 'Please select brand.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <SelectField
                                addNew={(newValue) => createCategory(newValue)}
                                label={"Category"}
                                options={categories}
                                selected={selectedCategory}
                                onSelect={(value) => setSelectedCategory(value)}
                                error={errors2?.category?.message}
                                register={register2("category", {
                                  required: 'Please select category.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <SelectField
                                disabled={selectedBrand === null}
                                addNewPopup={
                                  <span
                                    className="cursor-pointer text-primary text-bold"
                                    data-bs-toggle="modal"
                                    data-bs-target="#open-product-modal"
                                  >
                                    Add New
                                  </span>
                                }
                                label={"Product"}
                                options={products}
                                selected={selectedProduct}
                                onSelect={(value) => setSelectedProduct(value)}
                                error={errors2?.product?.message}
                                register={register2("product", {
                                  required: 'Please select product.'
                                })}
                              />
                            </div>
                          </div>
                          {variants?.length > 0 &&
                            <Fragment>
                              {variants.map((item, index) => (
                                <div key={index} className="col-lg-4 col-sm-6 col-12">
                                  <div className="mb-3 add-product">
                                    <label className="form-label">{item?.name}</label>
                                    <Controller
                                      control={control2}
                                      name={`variant-${item.name}`}
                                      rules={{
                                        required: `Select ${item?.name}`,
                                      }}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                          size="large"
                                          style={{ width: '100%' }}
                                          options={item?.options}
                                          onChange={(value) => { field.onChange(value); handleSelectVariant() }}
                                        />
                                      )}
                                    />
                                  </div>
                                </div>
                              ))}
                            </Fragment>
                          }
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <InputField
                                label="Quantity"
                                error={errors2?.quantity?.message}
                                register={register2("quantity", {
                                  required: 'Please enter quantity.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <InputField
                                disabled={true}
                                label="Reg Price"
                                error={errors2?.regPrice?.message}
                                register={register2("regPrice", {
                                  // required: 'Please enter reg price.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <InputField
                                label="Sale Price"
                                error={errors2?.salePrice?.message}
                                register={register2("salePrice", {
                                  required: 'Please enter sale price.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-5 col-sm-6 col-12">
                            <div className="mb-3">
                              <SelectField
                                label={"Shipping Type"}
                                options={deliveryTypes}
                                selected={selectedShippingType}
                                onSelect={(value) => setSelectedShippingType(value)}
                                error={errors2?.shippingType?.message}
                                register={register2("shippingType", {
                                  required: 'Please select shipping type.'
                                })}
                              />
                            </div>
                          </div>
                          <div className="col-lg-7 col-sm-12 col-12">
                            <div className="mb-3">
                              <InputField
                                label="Description"
                                inputType="textarea"
                                placeholder="Type Description"
                                register={register2("description")}
                              />
                            </div>
                          </div>
                          {selectedVariant !== null &&
                            <div className="col-lg-12 col-sm-12 col-12">
                              <div className="mb-3">
                                Total Available Quantity: {selectedVariant?.totalQuantity}
                              </div>
                            </div>
                          }
                        </div>
                        <div className="modal-footer-btn">
                          <button
                            id="modalClose"
                            type="button"
                            className="btn btn-cancel me-2"
                            data-bs-dismiss="modal"
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-submit"
                            type="submit"
                          >
                            Submit
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(submitForm)}>
            <div className="card">
              <div className="card-body add-product pb-0" style={{ padding: '1rem' }}>

                {/* Information & Billing & Shipping Details */}
                <div className="row">
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-2">
                      <div className="addproduct-icon list icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                        <h5>
                          <LifeBuoy className="add-info" />
                          <span>Billing Details</span>
                        </h5>
                      </div>
                      <div className="row">
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing First Name"
                              placeholder="Name"
                              type="text"
                              size="small"
                              error={errors?.billingName?.message}
                              register={register("billingName", {
                                required: 'Please enter billing name.',
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing Last Name"
                              placeholder="Name"
                              type="text"
                              size="small"
                              error={errors?.billingLastName?.message}
                              register={register("billingLastName", {
                                required: 'Please enter billing last name.',
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <Controller
                              name="billingPhone1"
                              control={control}
                              rules={{
                                required: "Please enter billing phone.",
                                pattern: {
                                  value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                  message: "Invalid phone number format"
                                }
                              }}
                              render={({ field }) => {
                                const inputRef = useRef(null);
                                useEffect(() => {
                                  if (inputRef.current) {
                                    const mask = IMask(inputRef.current, {
                                      mask: '(000) 000-0000'
                                    });
                                    mask.on('complete', () => {
                                      getCustomerByPhone(mask.value);
                                    });
                                    return () => mask.destroy();
                                  }
                                }, []);
                                return (
                                  <>
                                    <label className="form-label">Billing Phone 1</label>
                                    <input
                                      {...field}
                                      ref={(e) => {
                                        field.ref(e);
                                        inputRef.current = e;
                                      }}
                                      className={`form-control ${errors?.billingPhone1?.message ? 'is-invalid' : ''} `}
                                      style={{ height: '30px' }}
                                      placeholder="(123) 456-7890"
                                    />
                                    <div className="invalid-feedback">{errors?.billingPhone1?.message}</div>
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <Controller
                              name="billingPhone2"
                              control={control}
                              render={({ field }) => {
                                const inputRef = useRef(null);
                                useEffect(() => {
                                  if (inputRef.current) {
                                    const mask = IMask(inputRef.current, {
                                      mask: '(000) 000-0000'
                                    });
                                    return () => mask.destroy();
                                  }
                                }, []);
                                return (
                                  <>
                                    <label className="form-label">Billing Phone 2</label>
                                    <input
                                      {...field}
                                      ref={(e) => {
                                        field.ref(e);
                                        inputRef.current = e;
                                      }}
                                      className={`form-control ${errors?.billingPhone2?.message ? 'is-invalid' : ''} `}
                                      style={{ height: '30px' }}
                                      placeholder="(123) 456-7890"
                                    />
                                    <div className="invalid-feedback">{errors?.billingPhone2?.message}</div>
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing Email"
                              placeholder="Email"
                              type="email"
                              size="small"
                              // error={errors?.billingEmail?.message}
                              register={register("billingEmail", {
                                // required: 'Please enter billing email.',
                                // pattern: {
                                //   value: emailRegex,
                                //   message: 'Please enter a valid email.',
                                // }
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing Zip Code"
                              placeholder="Zip Code"
                              type="number"
                              size="small"
                              error={errors?.billingZipCode?.message}
                              register={register("billingZipCode", {
                                required: 'Please enter billing zip code.',
                                onChange: (e) => Debounce(() => getInfoByZipCode(e.target.value, 'billing'))
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing City"
                              placeholder="City"
                              type="text"
                              size="small"
                              register={register("billingCity")}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing State"
                              placeholder="State"
                              type="text"
                              size="small"
                              register={register("billingState")}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Billing Address"
                              placeholder="Address"
                              type="text"
                              size="small"
                              error={errors?.billingAddress?.message}
                              register={register("billingAddress", {
                                required: 'Please enter billing address.',
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="custom-control custom-checkbox">
                            <label className="checkboxs ps-4 mb-2 mt-1 pb-0 line-height-1">
                              <input type="checkbox" checked={sameDeliveryAddress} onChange={() => setSameDeliveryAddress(prev => !prev)} />
                              <span className="checkmarks" />
                              Delivery address is same as billing address.
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-2">
                      <div className="addproduct-icon list icon cursor-inherit" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                        <h5>
                          <LifeBuoy className="add-info" />
                          <span>Shipping Details</span>
                        </h5>
                      </div>
                      <div className="row">
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping First Name"
                              placeholder="Name"
                              type="text"
                              size="small"
                              error={errors?.shippingName?.message}
                              register={register("shippingName", {
                                required: sameDeliveryAddress ? false : 'Please enter shipping name.',
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping Last Name"
                              placeholder="Name"
                              type="text"
                              size="small"
                              error={errors?.shippingLastName?.message}
                              register={register("shippingLastName", {
                                required: sameDeliveryAddress ? false : 'Please enter shipping last name.',
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <Controller
                              name="shippingPhone1"
                              disabled={sameDeliveryAddress}
                              control={control}
                              rules={{
                                required: "Please enter shipping phone.",
                                pattern: {
                                  value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                  message: "Invalid phone number format"
                                }
                              }}
                              render={({ field }) => {
                                const inputRef = useRef(null);
                                useEffect(() => {
                                  if (inputRef.current) {
                                    const mask = IMask(inputRef.current, {
                                      mask: '(000) 000-0000'
                                    });
                                    return () => mask.destroy();
                                  }
                                }, []);
                                return (
                                  <>
                                    <label className="form-label">Shipping Phone 1</label>
                                    <input
                                      {...field}
                                      ref={(e) => {
                                        field.ref(e);
                                        inputRef.current = e;
                                      }}
                                      className={`form-control ${errors?.shippingPhone1?.message ? 'is-invalid' : ''} `}
                                      style={{ height: '30px' }}
                                      placeholder="(123) 456-7890"
                                    />
                                    <div className="invalid-feedback">{errors?.shippingPhone1?.message}</div>
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <Controller
                              name="shippingPhone2"
                              disabled={sameDeliveryAddress}
                              control={control}
                              render={({ field }) => {
                                const inputRef = useRef(null);
                                useEffect(() => {
                                  if (inputRef.current) {
                                    const mask = IMask(inputRef.current, {
                                      mask: '(000) 000-0000'
                                    });
                                    return () => mask.destroy();
                                  }
                                }, []);
                                return (
                                  <>
                                    <label className="form-label">Shipping Phone 2</label>
                                    <input
                                      {...field}
                                      ref={(e) => {
                                        field.ref(e);
                                        inputRef.current = e;
                                      }}
                                      className={`form-control ${errors?.shippingPhone2?.message ? 'is-invalid' : ''} `}
                                      style={{ height: '30px' }}
                                      placeholder="(123) 456-7890"
                                    />
                                    <div className="invalid-feedback">{errors?.shippingPhone2?.message}</div>
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping Email"
                              placeholder="Email"
                              type="email"
                              size="small"
                              // error={errors?.shippingEmail?.message}
                              register={register("shippingEmail", {
                                // required: sameDeliveryAddress ? false : 'Please enter shipping email.',
                                // pattern: {
                                //   value: emailRegex,
                                //   message: 'Please enter a valid email.',
                                // }
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping Zip Code"
                              placeholder="Zip Code"
                              type="number"
                              size="small"
                              error={errors?.shippingZipCode?.message}
                              register={register("shippingZipCode", {
                                required: sameDeliveryAddress ? false : 'Please enter shipping zip code.',
                                onChange: (e) => Debounce(() => getInfoByZipCode(e.target.value, 'shipping'))
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping City"
                              placeholder="City"
                              type="text"
                              size="small"
                              register={register("shippingCity")}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping State"
                              placeholder="State"
                              type="text"
                              size="small"
                              register={register("shippingState")}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              disabled={sameDeliveryAddress}
                              label="Shipping Address"
                              placeholder="Address"
                              type="text"
                              size="small"
                              error={errors?.shippingAddress?.message}
                              register={register("shippingAddress", {
                                required: sameDeliveryAddress ? false : 'Please enter shipping address.',
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-2">
                      <div className="addproduct-icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                        <h5>
                          <Info className="add-info" />
                          <span>Information</span>
                        </h5>
                      </div>
                      <div className="row">
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <div className="input-blocks mb-0">
                              <label>Order Date</label>
                              <div className="input-groupicon calender-input">
                                <Calendar className="info-img" />
                                <Controller
                                  name="order_date"
                                  control={control}
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker
                                      className="datetimepicker"
                                      placeholder="Order Date"
                                      format={DateFormat}
                                      minDate={userData?.role_id !== 1 ? dayjs() : undefined}
                                      onChange={(date) => onChange(date || null)}
                                      onBlur={onBlur}
                                      value={value ? dayjs(value, DateFormat) : null}
                                      ref={ref}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <div className="input-blocks mb-0">
                              <label className="form-label">Delivery Date</label>
                              <div className="input-groupicon calender-input">
                                <Calendar className="info-img" />
                                <Controller
                                  name="delivery_date"
                                  control={control}
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker
                                      className="datetimepicker"
                                      placeholder="Delivery Date"
                                      format={DateFormat}
                                      minDate={userData?.role_id !== 1 ? dayjs() : undefined}
                                      onChange={(date) => onChange(date || null)}
                                      onBlur={onBlur}
                                      value={value ? dayjs(value, DateFormat) : null}
                                      ref={ref}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <SelectField
                              label={"Delivery Time"}
                              placeholder={"Delivery Time"}
                              options={deliveryTimeSlots}
                              selected={selectedDeliveryTimeSlot}
                              onSelect={(value) => setSelectedDeliveryTimeSlot(value)}
                              error={errors?.deliveryTimeSlot?.message}
                              register={register("deliveryTimeSlot", {
                                required: 'Please select delivery time slot.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <SelectField
                              label={"Delivery Type"}
                              placeholder={"Delivery Type"}
                              options={deliveryTypes}
                              selected={selectedDeliveryType}
                              onSelect={(value) => setSelectedDeliveryType(value)}
                              error={errors?.deliveryType?.message}
                              register={register("deliveryType", {
                                required: 'Please select delivery type.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="mb-1 add-product">
                            <SelectField
                              label={"Sale Person"}
                              placeholder={"Sale Person"}
                              multiple={true}
                              options={salePersons}
                              selected={selectedSalePerson}
                              onSelect={(value) => { setSelectedSalePerson(value); setValue('salePerson', value); }}
                            // error={errors?.salePerson?.message}
                            // register={register("salePerson", {
                            //   required: 'Please select sale person.'
                            // })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="mb-1 add-product">
                            <SelectField
                              label={"Store"}
                              placeholder={"Store"}
                              options={stores}
                              selected={selectedStore}
                              onSelect={(value) => setSelectedStore(value)}
                              error={errors?.store?.message}
                              register={register("store", {
                                required: 'Please select store.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-12 col-sm-12 col-12">
                          <div className="mb-1 add-product">
                            <SelectField
                              label={"Status"}
                              placeholder={"Status"}
                              options={statuses.map((status) => ({ name: status.label, id: status.value }))}
                              selected={selectedStatus}
                              onSelect={(value) => setSelectedStatus(value)}
                              error={errors?.status?.message}
                              register={register("status", {
                                required: 'Please select status.'
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sale */}
                <div className="mb-2">
                  <div className="addproduct-icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                  </div>
                  <div className="row">
                    <div className="col-lg-3 col-sm-3 col-6 my-2">
                      <div className="mb-1 add-product">
                        <InputField
                          placeholder="Scan here"
                          type="text"
                          error={errors3?.barcode?.message}
                          register={register3("barcode", {
                            required: 'Please enter sku/barcode.',
                          })}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-3 col-6 my-2">
                      <button type="button"
                        className="btn btn-submit me-2"
                      // onClick={handleSubmit3(getProductByBarcode)}
                      // data-bs-toggle="modal"
                      // data-bs-target="#open-modal"
                      >
                        Search
                      </button>

                      <button
                        type="button"
                        className="btn btn-submit me-2"
                        onClick={() => setScanOpen(!scanOpen)}
                      >
                        Scan
                      </button>

                      <button type="button"
                        className="btn btn-info"
                        data-bs-toggle="modal"
                        data-bs-target="#open-modal"
                        onClick={() => resetAddRowFields()}
                      >
                        Add Manual
                      </button>

                      {scanOpen && (
                        <Html5QrcodePlugin
                          fps={10}
                          qrbox={250}
                          disableFlip={false}
                          qrCodeSuccessCallback={onNewScanResult}
                        />
                      )}
                    </div>
                  </div>
                  {mergedRows.length > 0 &&
                    <Table
                      className="table datanew dataTable no-footer custom-table"
                      columns={columns}
                      dataSource={mergedRows}
                      pagination={false}
                    />
                  }
                  <div className="row mt-1 mt-1">
                    <div className="col-lg-3 col-sm-6 col-12">
                      <button type="button"
                        className="btn btn-info ms-2"
                        onClick={() => setShowDiscountModal(true)}
                        style={{ height: '34px', marginLeft: '8px', marginTop: '2px' }}
                      >
                        Add Discount Item
                      </button>
                    </div>
                  </div>
                  <div className="row mt-3 mb-1">
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="mb-0">
                        <SelectField
                          placeholder={"Service Type"}
                          options={serviceTypes}
                          selected={selectedServiceType}
                          onSelect={(value) => { setSelectedServiceType(value); setDeliveryDiscount(0); setValue('deliveryDiscount', 0) }}
                          error={errors?.serviceType?.message}
                          register={register("serviceType", {
                            required: 'Please select service type.'
                          })}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-6 col-12">
                      {selectedServiceType &&
                        <p>{selectedServiceType?.remarks} {`(charges ${parseFloat(selectedServiceType?.price).toFixed(2)})`}</p>
                      }
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="total-order w-100 max-widthauto m-auto mb-2">
                        <ul>
                          {selectedServiceType?.price > 0 &&
                            <li>
                              <h4>Delivery Discount</h4>
                              <h5 className="py-0">
                                <InputField
                                  placeholder={`Discount`}
                                  type="text"
                                  size="small"
                                  register={register(`deliveryDiscount`, {
                                    onChange: (e) => setDeliveryDiscount(e.target.value || 0)
                                  })}
                                />
                              </h5>
                            </li>
                          }
                          {/*<li>
                            <h4>Total</h4>
                            <h5>$ {parseFloat(total).toFixed(2)}</h5>
                          </li>*/}
                          <li>
                            <h4>Saving</h4>
                            <h5>$ {parseFloat(discountTotal || 0).toFixed(2)}</h5>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="col-lg-6 ms-auto">
                      <div className="total-order w-100 max-widthauto m-auto mb-4">
                        <ul>
                          <li>
                            <h4>Sub Total</h4>
                            <h5>$ {parseFloat(subTotal).toFixed(2)}</h5>
                          </li>
                          <li>
                            <h4>Total Discounts</h4>
                            <h5>$ {parseFloat(discountTotal || 0).toFixed(2)}</h5>
                          </li>
                          <li>
                            <h4>Sale Tax</h4>
                            <h5>$ {parseFloat(saleTaxRate).toFixed(2)}</h5>
                          </li>
                          <li>
                            <h4>Grand Total</h4>
                            <h5>$ {(Number(parseFloat(subTotal || 0)) - Number(parseFloat(discountTotal || 0)) + Number(parseFloat(saleTaxRate || 0))).toFixed(2)}</h5>
                          </li>
                          {invoicePayments?.length > 0 &&
                            invoicePayments.map((item, index) => (
                              <li key={index} className="justify-content-between">
                                <h4 className="d-flex align-items-center gap-2 justify-content-between">
                                  <span>Amount Received {index + 1}</span>
                                  <SelectField
                                    style={{ width: "60%" }}
                                    placeholder={"Type"}
                                    options={paymentMethods}
                                    selected={item?.payment_head ?? null}
                                    onSelect={(value) => handleUpdateInvoicePayment(value, item.id, 'payment_head')}
                                    disabled={id && !item.isNew}
                                  />
                                  <InputField
                                    placeholder={`Card last 4 digit`}
                                    type="text"
                                    register={register(`card_last_digit-${item?.id}`, {
                                      onChange: (e) => Debounce(() => handleUpdateInvoicePayment(e.target.value, item.id, 'card_last_digit'))
                                    })}
                                    disabled={id && !item.isNew}
                                  />
                                </h4>
                                <InputField
                                  placeholder={`Received Amount ${index + 1}`}
                                  type="text"
                                  register={register(`received_amount-${item?.id}`, {
                                    onChange: (e) => Debounce(() => handleUpdateInvoicePayment(parseFloat(e.target.value), item.id, 'received_amount'))
                                  })}
                                  disabled={id && !item.isNew}
                                />
                              </li>
                            ))
                          }
                          <li>
                            <h4>Balance Amount</h4>
                            <h5>$ {balanceAmount.toFixed(2)}</h5>
                          </li>
                        </ul>
                      </div>
                      <button
                        type="button"
                        className="btn btn-submit mb-3"
                        onClick={() => addInvoicePayment()}
                      >
                        Add Payment Method
                      </button>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6 col-sm-6 col-12">
                      <div className="input-blocks">
                        <InputField
                          label="Comment"
                          type="text"
                          register={register("comments")}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-6 col-12">
                      <div className="input-blocks">
                        <InputField
                          label="Instructions"
                          type="text"
                          register={register("instructions")}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <div className="col-lg-12">
              <div className="btn-addproduct mb-4">
                <Link to={route.sales} className="btn btn-cancel me-2">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-submit"
                  disabled={loading}
                >
                  Submit
                </button>
              </div>
            </div>
            {/* Discount Modal */}
            {showDiscountModal && (
              <div className="modal fade show" style={{ display: 'block', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered custom-modal-two" style={{ maxWidth: '600px', margin: 'auto', position: 'relative', zIndex: 100000 }}>
                  <div className="modal-content" style={{ zIndex: 100001 }}>
                    <div className="page-wrapper-new p-0">
                      <div className="content">
                        <div className="modal-header border-0 custom-modal-header">
                          <div className="page-title">
                            <h4>Add Discount Item</h4>
                          </div>
                          <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => setShowDiscountModal(false)}
                          >
                            <span aria-hidden="true">×</span>
                          </button>
                        </div>
                        <div className="modal-body custom-modal-body new-employee-field">
                          <div className="row">
                            <div className="col-12 mb-3">
                              <InputField
                                label="Description"
                                placeholder="Discount description"
                                register={{
                                  value: discountDescription,
                                  onChange: (e) => setDiscountDescription(e.target.value),
                                }}
                              />
                            </div>
                            <div className="col-6 mb-3">
                              <label className="form-label">Type</label>
                              <Select
                                size="large"
                                style={{ width: '100%' }}
                                value={discountType}
                                options={[
                                  { label: <span>Amount</span>, value: 'amount', key: 1 },
                                  { label: <span>Percentage</span>, value: 'percentage', key: 2 }
                                ]}
                                onChange={(val) => setDiscountType(val)}
                              />
                            </div>
                            <div className="col-6 mb-3">
                              <InputField
                                label={discountType === 'percentage' ? 'Value (%)' : 'Amount'}
                                placeholder={discountType === 'percentage' ? 'e.g., 10' : 'e.g., 50.00'}
                                register={{
                                  value: discountValue,
                                  onChange: (e) => setDiscountValue(e.target.value),
                                }}
                              />
                            </div>
                            <div className="col-12 d-flex justify-content-end gap-2">
                              <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={() => {
                                  setShowDiscountModal(false);
                                  setDiscountDescription('');
                                  setDiscountType('amount');
                                  setDiscountValue('');
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-submit"
                                onClick={() => submitDiscountRow()}
                              >
                                Add Discount Item
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-backdrop fade show" style={{ zIndex: 99990 }} onClick={() => setShowDiscountModal(false)}></div>
              </div>
            )}
          </form>

          {/* Refund/Exchange Modal */}
          {showRefundModal && saleInvoice && saleInvoice.sale_invoice_items && (
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Refund / Exchange</h5>
                    <button type="button" className="btn-close" onClick={() => setShowRefundModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <RefundExchangeForm
                      preselectedInvoices={[saleInvoice]}
                      preselectedItems={saleInvoice.sale_invoice_items.map(item => ({ ...item, invoiceId: saleInvoice.id }))}
                      onSubmit={() => setShowRefundModal(false)}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show" onClick={() => setShowRefundModal(false)}></div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Form;

